<script lang="ts" setup>
import type { GroupMember } from '~/models/Group'
import type { Task, TaskUser } from '~/models/AssignTask'
import { useI18n } from 'vue-i18n'
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import useGroupsStore from '~/stores/groups'
import useAssignTaskDialog from '~/stores/assignTaskDialog'
import { UserRole } from '~/models/User/UserRole'
import { PendoFeature } from '~/models/Pendo'
import { TaskStatus } from '~/models/AssignTask'
import useTime from '~/composables/useTime'
import { useAssignTask } from '~/composables/useAssignTask'
import Button from '~/components/ui/Button.vue'
import AuSpinner from '~/components/ui/AuSpinner.vue'
import AuDialog from '~/components/ui/AuDialog.vue'
import AuCallout from '~/components/ui/AuCallout.vue'
import StudentSelectionList from '~/components/products/exams/assignment/StudentSelectionList.vue'
import DeleteTaskDialog from '~/components/products/exams/assignment/DeleteTaskDialog.vue'

const { task } = defineProps<{ task: Task }>()

const { t } = useI18n()
const { loadGroupMembersById, findGroupMembersById } = useGroupsStore()
const { isLoading } = storeToRefs(useGroupsStore())
const { taskHasBeenClosed } = storeToRefs(useAssignTaskDialog())
const { addTaskUsers: add, addTaskUsersAndOpen } = useAssignTask()
const { setDialogOpen, setDialogClose, isDialogOpen } = useAssignTaskDialog()
const { currentWeekNumber } = useTime()

const students = ref<TaskUser[]>(task.users ?? [])
const showClosedMessage = ref(task.taskId ? taskHasBeenClosed.value[task.taskId] : false)

const isOpen = computed(() => task.taskId && isDialogOpen(task.taskId, 'assign'))
const disabled = computed(() => !students.value.length || add.isPending.value || addTaskUsersAndOpen.isPending.value)
const taskUserRequest = computed(() => ({
  users: students.value.map(({ userId, status }) => ({ userId, status })),
}))

const openDialog = () => task.taskId && setDialogOpen(task.taskId, 'assign')
const closeDialog = () => task.taskId && setDialogClose(task.taskId, 'assign')

const addUsersAndOpenTask = async () => {
  if (!task.taskId || !students.value.length)
    return
  await addTaskUsersAndOpen.mutateAsync({
    task: { ...task, week: currentWeekNumber },
    users: taskUserRequest.value
  })
  closeDialog()
  setDialogOpen(task.taskId, 'edit')
}

const addTaskUsers = async () => {
  if (!task.taskId || !students.value.length)
    return
  await add.mutateAsync({ task, users: taskUserRequest.value })
  closeDialog()
}

const filterStudents = (groupMembers: GroupMember[]): GroupMember[] =>
  groupMembers.filter(student => student.role === UserRole.Student)

watch(isOpen, async (open) => {
  if (open) {
    if (task.users?.length) {
      students.value = task.users
    } else if (task.groups) {
      await loadGroupMembersById(task.groups[0])
      const allStudents = findGroupMembersById(task.groups[0])
      students.value = filterStudents(allStudents)
        .map(({ userId, firstName, lastName, fullName, role }) => ({
          userId,
          status: TaskStatus.Open,
          firstName,
          lastName,
          fullName,
          primaryRole: role,
        }))
    }
  }
})

watch(isOpen, () => {
  if (!task.taskId)
    return
  if (taskHasBeenClosed.value[task.taskId]) {
    showClosedMessage.value = true
  }
})
</script>

<template>
  <AuDialog
    :open="isOpen || false"
    :title="task.name ?? ''"
    size="lg"
    @update:open="val => !val && closeDialog()"
  >
    <template #trigger>
      <Button
        color="blue"
        shade="light"
        @click="openDialog"
      >
        {{ t('assign.editAccess') }}
      </Button>
    </template>
    <template #content>
      <div class="flex flex-col gap-4">
        <AuCallout
          v-if="showClosedMessage"
          :title="t('assign.toasts.closeSuccess')"
          variant="info"
        />
        <p
          v-else
          v-text="t('assign.chooseStudentsAccess')"
        />
        <p v-if="!showClosedMessage" v-text="t('assign.studentsAccessInfo')" />
        <StudentSelectionList
          v-if="!isLoading"
          v-model="students"
          :task-id="task.taskId"
          @close-modal="closeDialog"
        />
        <AuSpinner v-else />
        <div class="flex justify-end gap-4">
          <DeleteTaskDialog
            :disable-btn="add.isPending.value || addTaskUsersAndOpen.isPending.value"
            :task
          />
          <Button
            :data-pendo="PendoFeature.TermExam.SaveTask"
            :disabled
            size="lg"
            variant="outline"
            @click="addTaskUsers"
          >
            <div class="flex gap-1">
              {{ add.isPending.value ? t('assign.savingStudents') : t('assign.saveStudents') }}
              <AuSpinner
                v-if="add.isPending.value"
                size="sm"
                color="var(--color-seagreen-30)"
              />
            </div>
          </Button>
          <Button
            :data-pendo="PendoFeature.TermExam.StartTask"
            :disabled
            size="lg"
            @click="addUsersAndOpenTask"
          >
            <div class="flex gap-1">
              {{ addTaskUsersAndOpen.isPending.value ? t('assign.startingExam') : t('assign.startExam') }}
              <AuSpinner
                v-if="addTaskUsersAndOpen.isPending.value"
                size="sm"
                color="var(--color-seagreen-30)"
              />
            </div>
          </Button>
        </div>
      </div>
    </template>
  </AuDialog>
</template>
